import { ReportContextDisplay } from '../ReportContext';
import { useInfiniteQuery } from '@tanstack/react-query';
import { api } from '../../../api/client';
import type { TicketDetail } from '../../../api/ticket-types';
import type { AuditEvent, Page } from '../../../api/types';
import { QueryView } from '../../../ui/feedback';
import { Markdown } from '../../../ui/Markdown';
import { ActorName, Card, LoadMore } from '../../../ui/misc';
import { Timestamp } from '../../../ui/Timestamp';
import { EventTable } from '../../shared/EventTable';

export function History({ t }: { t: TicketDetail }) {
  const q = useInfiniteQuery({
    queryKey: ['ticket-activity', t.project.key, t.number],
    queryFn: ({ pageParam }) =>
      api.get<Page<AuditEvent>>(
        `/projects/${t.project.key}/tickets/${t.number}/activity${pageParam ? `?cursor=${pageParam}` : ''}`,
      ),
    initialPageParam: '',
    getNextPageParam: (l) => l.nextCursor ?? undefined,
  });
  return (
    <>
      <Card title="Scope revisions">
        {t.revisions.map((r) => (
          <details key={r.revision} open={r.revision === t.scopeRevision}>
            <summary style={{ cursor: 'pointer' }}>
              <span className="strong">Revision {r.revision}</span>{' '}
              <span className="small muted">
                · <ActorName actor={r.author} /> · <Timestamp value={r.createdAt} />
                {r.reason ? ` · ${r.reason}` : ''}
              </span>
            </summary>
            <div className="well stack sm" style={{ marginTop: 8 }}>
              <span className="strong">{r.title}</span>
              <Markdown>{r.description}</Markdown>
              <ReportContextDisplay value={r} />
              {r.stepsOrOutcome && (
                <>
                  <h3 className="title-sm">
                    {r.type === 'issue' ? 'Reproduction steps' : 'Intended outcome'}
                  </h3>
                  <Markdown>{r.stepsOrOutcome}</Markdown>
                </>
              )}
              {r.acceptanceCriteria.length > 0 && (
                <ol style={{ margin: 0, paddingLeft: 20 }}>
                  {r.acceptanceCriteria.map((c, i) => (
                    <li key={i}>
                      <Markdown>{c}</Markdown>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </details>
        ))}
      </Card>
      <Card title="Activity">
        <QueryView query={q}>
          {(d) => (
            <>
              <EventTable events={d.pages.flatMap((p) => p.items)} />
              <LoadMore
                hasMore={!!q.hasNextPage}
                onClick={() => q.fetchNextPage()}
                loading={q.isFetchingNextPage}
              />
            </>
          )}
        </QueryView>
      </Card>
    </>
  );
}
